import React from 'react';

const MessageInfoModal = ({ isOpen, onClose, message, messageType }) => {
  if (!isOpen || !message) return null;

  const renderPromptInfo = () => {
    if (messageType === 'user_input' && (message.prompt || message.metadata?.prompt_sent || message.metadata?.actual_prompt_sent)) {
      const promptText = message.prompt || message.metadata?.prompt_sent || message.metadata?.actual_prompt_sent;
      
      return (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Prompt Sent to LLM</h3>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
              {promptText}
            </pre>
          </div>
          
          {message.metadata?.structured_json && (
            <div className="mt-4">
              <h4 className="text-md font-medium text-gray-800 mb-2">Structured JSON</h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono">
                  {JSON.stringify(message.metadata.structured_json, null, 2)}
                </pre>
              </div>
            </div>
          )}
          
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Model:</span>
              <span className="ml-2 text-gray-600">{message.metadata?.model_used || 'N/A'}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Provider:</span>
              <span className="ml-2 text-gray-600">{message.metadata?.provider_used || 'N/A'}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderResponseInfo = () => {
    if (messageType === 'llm_response' && message.metadata) {
      return (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">LLM Response Metadata</h3>
          
          <div className="space-y-4">
            {message.metadata.tokens_used && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-blue-800">Tokens Used:</span>
                  <span className="text-blue-600 font-mono">{message.metadata.tokens_used}</span>
                </div>
              </div>
            )}
            
            {message.metadata.model_used && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-green-800">Model:</span>
                  <span className="text-green-600 font-mono">{message.metadata.model_used}</span>
                </div>
              </div>
            )}
            
            {message.metadata.original_message && (
              <div>
                <h4 className="text-md font-medium text-gray-800 mb-2">Original Message</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-sm text-gray-700">{message.metadata.original_message}</p>
                </div>
              </div>
            )}
            
            {message.metadata.actual_prompt_sent && (
              <div>
                <h4 className="text-md font-medium text-gray-800 mb-2">Actual Prompt Sent</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono">
                    {message.metadata.actual_prompt_sent}
                  </pre>
                </div>
              </div>
            )}
            
            {message.metadata.structured_json_used && (
              <div>
                <h4 className="text-md font-medium text-gray-800 mb-2">Structured JSON Used</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono">
                    {JSON.stringify(message.metadata.structured_json_used, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            
            {message.metadata.llm_metadata && Object.keys(message.metadata.llm_metadata).length > 0 && (
              <div>
                <h4 className="text-md font-medium text-gray-800 mb-2">Raw LLM Metadata</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono">
                    {JSON.stringify(message.metadata.llm_metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="absolute top-0 bottom-0 right-0 w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Message Information
            </h2>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-800 transition-colors font-bold text-2xl bg-transparent border-none"
            >
              ✕
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Message Type Badge */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-500">Type:</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  messageType === 'user_input' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {messageType === 'user_input' ? 'User Input' : 'LLM Response'}
                </span>
              </div>
              
              {/* Message Content */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Message Content</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{message.text}</p>
                </div>
              </div>
              
              {/* Prompt Information (for user messages) */}
              {renderPromptInfo()}
              
              {/* Response Information (for LLM messages) */}
              {renderResponseInfo()}
              
              {/* Timestamp */}
              <div className="text-sm text-gray-500">
                <span className="font-medium">Timestamp:</span> {message.timestamp}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageInfoModal; 